import {
  IonContent,
  IonPage,
  IonHeader,
  IonModal,
  IonToolbar,
  IonTitle,
  useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { arrowBack, menuOutline, optionsOutline } from "ionicons/icons";
import { ReactElement, useState } from "react";
import { useConfig } from "../../../contexts/ConfigContext";
import Icon from "../../atoms/Icon";
import Menu from "../../molecules/Menu/Menu";
import { useResonanceLayout } from "./ResonanceLayoutContext";
import ScreenTitle from "../../atoms/ScreenTitle";
import { MINI_LOGO, LOGO } from "../../../constants/images";
import { useHistory } from "react-router";
import { useStudySlug } from "../../../hooks/useStudySlug";
import styled from "styled-components";
import IslandSettings from "../../../pages/MentalPlusPages/IslandSettings";

interface ResonanceLayoutProps {
  children?: ReactElement;
  isConnected?: boolean;
}

const ResonanceLayout = ({
  children,
  isConnected,
}: ResonanceLayoutProps): ReactElement => {
  const {
    disableNav,
    disableHeader,
    isMenuVisible,
    setResonanceLayout,
    backgroundColor,
    title,
    welcomePage,
    headerBackground,
    headerLeftContent,
    headerRightContent,
    headerBackFallbackPath,
  } = useResonanceLayout();
  const [isIslandSettingsOpen, setIsIslandSettingsOpen] = useState(false);

  let history = useHistory();
  const ionRouter = useIonRouter();
  const study_slug = useStudySlug();
  const { t } = useTranslation();

  const { app } = useConfig();

  const setIsMenuVisible = (value: boolean) => {
    setResonanceLayout({
      isMenuVisible: value,
    });
  };

  // useEffect(() => {
  //   updateManifest(app?.name, app?.logo);
  // }, [app]);

  const handleLogoClick = () => {
    history.push(`/${study_slug}/home`); // Navigate to home page
  };

  const handleBackClick = () => {
    if (ionRouter.canGoBack()) {
      ionRouter.goBack();
      return;
    }

    if (headerBackFallbackPath) {
      history.push(headerBackFallbackPath);
    }
  };

  const renderHeaderLeftContent = () => {
    if (welcomePage || headerLeftContent === "none") {
      return null;
    }

    if (headerLeftContent === "back") {
      return (
        <HeaderBackButton
          type="button"
          onClick={handleBackClick}
          aria-label={t("common_previous")}
        >
          <Icon icon={arrowBack} />
        </HeaderBackButton>
      );
    }

    return (
      <Logo
        src={MINI_LOGO}
        // onClick={handleLogoClick}
        // role="button"
        // alt="Home"
      />
    );
  };

  const handleHeaderRightClick = () => {
    if (headerRightContent === "none") {
      return;
    }

    if (headerRightContent === "settings") {
      setIsIslandSettingsOpen(true);
      return;
    }

    setIsMenuVisible(true);
  };

  const renderHeaderRightIcon = () => {
    if (headerRightContent === "none") {
      return null;
    }

    if (headerRightContent === "settings") {
      return <Icon icon={optionsOutline} size="large" />;
    }

    return <Icon icon={menuOutline} size="large" />;
  };

return (
   <IonPage>
  {!disableHeader && welcomePage !== undefined && (
    <IonHeader className="ion-no-border">
      <WaveToolbar $headerBg={headerBackground}
      >
        <IonTitle>
          {!welcomePage && <ScreenTitle>{t(title)}</ScreenTitle>}
        </IonTitle>

        {renderHeaderLeftContent()}
        {headerRightContent !== "none" && (
          <MenuButton onClick={handleHeaderRightClick}>
            {renderHeaderRightIcon()}
          </MenuButton>
        )}
      </WaveToolbar>
    </IonHeader>
  )}

  {welcomePage && <BigLogoWrapper><BigLogo /></BigLogoWrapper>}

  <IonContent className={!disableNav ? "with-tabbar" : "no-tabbar"}
    style={{
      position: "relative",
      backgroundColor,
      display: "flex",
      flexDirection: "column",
      maxWidth: "1200px",
      margin: "0 auto",
      width: "100%",
      alignSelf: "center",
    }}
    fullscreen={true}
  >
    {children}
    {/* {isConnected && !disableNav && <NavButtons />} */}
  </IonContent>

  {isMenuVisible && (
    <Menu
      onClose={() => setIsMenuVisible(false)}
      isConnected={isConnected}
    />
  )}

  <IonModal
    className="resonance-sheetmodal"
    isOpen={isIslandSettingsOpen}
    backdropDismiss={true}
    breakpoints={[0, 1]}
    initialBreakpoint={1}
    canDismiss={true}
    onDidDismiss={() => setIsIslandSettingsOpen(false)}
  >
    <IonToolbar>
      <IonTitle>Island Setting</IonTitle>
      <CloseButton slot="end" onClick={() => setIsIslandSettingsOpen(false)}>
        x
      </CloseButton>
    </IonToolbar>

    <IslandSettings />
  </IonModal>
</IonPage>

  );
};

const Logo = styled.img`
  position: absolute;
  max-width: 12.5%;
  max-height: 7vh;
  top: 0;
  bottom: 0;
  left: 0;
  margin: auto 0;
  // cursor: pointer;
`;

const HeaderBackButton = styled.button`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  height: 46px;
  padding: 0 6px 0 2px;
  background-color: ${(props) => props.theme.secondary.main};
  color: ${(props) => props.theme.secondary.contrast};
  border: none;
  border-radius: 0 24px 24px 0;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;

  @media (min-width: 800px) {
    border-radius: 24px;
    left: 12px;
  }
`;

const BigLogoWrapper = styled.div`
  position: absolute;
  top: calc(25vh * (163 / 500)); /* Bottom of the toolbar */
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center; /* Center the logo horizontally */
  transform: translateY(-50%); /* Center vertically on toolbar bottom */
  z-index: 10;
  pointer-events: none;
`;


const BigLogo = styled.img.attrs({
  src: LOGO,
})`
  max-width: 18%;
  max-height: 16vh;

  @media only screen and (max-width: 480px) {
    max-height: 18vh;
    max-width: 30%;
  }
`;


const MenuButton = styled.button`
   position: absolute;
  right: 15px;
  top: 0;
  bottom: 0;
  margin: auto 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: inherit;
  font-size: 28px;
  line-height: 1;
  padding: 8px 12px;
  cursor: pointer;
`;

const WaveToolbar = styled(IonToolbar)<{ $headerBg?: string }>`
  padding: 0;
  position: relative;
  display: flex;
  height: calc(38vh * (163 / 500));

  /* Ionic internals */
  --opacity: 0;
  --background: transparent;

  /* What you had inline */
  background-color: ${({ $headerBg }) => $headerBg};;

  background-image: url("/assets/images/header-background-main.svg");
  background-position: left top;
  background-repeat: repeat-x;
  background-size: auto 100%;

  /* iOS Safari seam fix */
  @supports (-webkit-touch-callout: none) {
    background-size: auto 101.3%;
  }
`;

export default ResonanceLayout;